import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { forms } from '@cwos/api-client';
import { Pill, SuccessEmpty, TableCard, useTabListKeys, type Column } from '@cwos/ui-admin';
import { usePermissions } from '../permissions.js';
import { authorityGate, gateSentence } from '../authorityGate.js';
import { listPhase } from '../listState.js';
import { ListState } from '../ListStates.js';
import { useNav } from '../navigation.js';
import { SettingsCard, SettingsField, SettingsHeader } from '../settings/common.js';
import { INSTRUMENT_READING, SUBJECT_READING, formatWhen, messageOf } from './formsLogic.js';
import { validateFormKey } from './formsAuthoring.js';

/**
 * Forms — the organization's instruments, and the state each of them is in.
 *
 * FOURTEEN ROUTES AND NO DOOR. The forms domain has carried a complete
 * author → review → approve → publish → assign → respond lifecycle since
 * migration 1758500000000, with a structural publication gate and an
 * append-only approval record, and nothing in either app could call any of it.
 *
 * WHAT THIS LIST CAN AND CANNOT SAY. `GET /api/forms` returns DEFINITIONS —
 * key, title, instrument kind, who it can be about, active. It carries no
 * versions, so this screen genuinely does not know whether a given form is
 * published, in review or still a draft, and it says "not checked" rather than
 * guessing. The detail screen asks, and answers. Fetching every definition's
 * detail to fill a column would be a dozen requests to render a word.
 *
 * ACTIVE IS NOT PUBLISHED. `active: false` is the DEFINITION being retired;
 * a definition can be active with no published version at all. The filter says
 * which it is filtering on.
 */
const TABS = [
  { key: 'active' as const, label: 'In use' },
  { key: 'all' as const, label: 'Everything' },
];

export function FormsCenter() {
  const permissions = usePermissions();
  const { has } = permissions;
  const navigate = useNav();
  const qc = useQueryClient();
  const canAuthor = has(forms.FORMS_AUTHOR);
  /**
   * WHY THIS IS A GATE AND NOT `responseReachScope(has).kind === 'none'`.
   *
   * `none` is what `responseReachScope` answers for a reader who does not hold
   * `forms:read_responses` — and, because `has()` is default-deny, also for one
   * whose permission set has not arrived and one whose read of it failed. The
   * note below is a sentence about the reader's ACCOUNT ("that is a permission
   * your organization controls"), and rendering it from either of those two
   * accuses somebody of lacking a key they may hold. The scope value is not
   * wrong — the reachability sentence in `FormResponseReader` is still built
   * from it — but the sentence must be claimed from a resolved set.
   */
  const responseGate = authorityGate(permissions, forms.FORMS_READ_RESPONSES);

  const [creating, setCreating] = useState(false);
  const [newForm, setNewForm] = useState({
    formKey: '',
    title: '',
    purpose: '',
    instrumentKind: 'assessment' as forms.FormInstrumentKind,
    subjectKinds: ['client'] as forms.FormSubjectKind[],
  });
  const [createError, setCreateError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      forms.createDefinition({
        formKey: newForm.formKey.trim(),
        title: newForm.title.trim(),
        ...(newForm.purpose.trim() === '' ? {} : { purpose: newForm.purpose.trim() }),
        instrumentKind: newForm.instrumentKind,
        subjectKinds: newForm.subjectKinds,
      }),
    onSuccess: (definition) => {
      setCreating(false);
      setCreateError(null);
      void qc.invalidateQueries({ queryKey: ['forms'] });
      // Straight to the detail screen, because a definition with no version
      // asks nobody anything — the next thing to do is write the questions.
      navigate(`/compliance/forms/${definition.id}`);
    },
    onError: (err: unknown) => setCreateError(messageOf(err)),
  });
  const formKeyProblem = validateFormKey(newForm.formKey);

  const [tab, setTab] = useState<'active' | 'all'>('active');
  const tabKeys = useTabListKeys<HTMLDivElement>();
  const [kind, setKind] = useState<forms.FormInstrumentKind | ''>('');

  const list = useQuery({
    queryKey: ['forms', 'definitions', tab, kind],
    queryFn: () =>
      forms.definitions({
        activeOnly: tab === 'active',
        ...(kind === '' ? {} : { instrumentKind: kind }),
      }),
  });

  const definitions = list.data ?? [];
  const phase = listPhase(list, definitions.length);
  const filtered = kind !== '' || tab === 'active';

  const columns: Column<forms.FormDefinitionView>[] = [
    {
      key: 'title',
      header: 'Form',
      render: (d) => (
        <span>
          {d.title}
          <br />
          <span className="rn-muted">{d.formKey}</span>
        </span>
      ),
    },
    { key: 'kind', header: 'Kind', render: (d) => INSTRUMENT_READING[d.instrumentKind] },
    {
      key: 'subjects',
      header: 'Can be about',
      render: (d) => d.subjectKinds.map((s) => SUBJECT_READING[s]).join(', '),
    },
    {
      key: 'state',
      header: 'Publication',
      // NOT ASKED, and it says so. The list endpoint carries no versions, so
      // "no published version" would be a claim this screen has no basis for —
      // and it is the claim that matters most, because a reader who believes it
      // will go and build a form that already exists.
      render: () => <span className="rn-muted">Open to check</span>,
    },
    {
      key: 'active',
      header: '',
      render: (d) =>
        d.active ? null : (
          <Pill variant="plain" dot>
            Retired
          </Pill>
        ),
    },
    { key: 'created', header: 'Created', render: (d) => formatWhen(d.createdAt) },
  ];

  return (
    <div className="rn-col" style={{ maxWidth: 1080 }}>
      <SettingsHeader
        title="Forms"
        blurb="The instruments this organization asks with — assessments, screenings, check-ins, incident reports. Nothing publishes that a person has not approved, and an approval is bound to the exact wording it approved."
      />

      <SettingsCard>
        <div className="cmp-control-head">
          <h2 className="rn-card-title">Read a response</h2>
          {canAuthor && !creating ? (
            <button type="button" className="rn-btn" onClick={() => setCreating(true)}>
              New form
            </button>
          ) : null}
        </div>
        <p className="rn-muted">
          A response answers one version of one form, and Phibian shows you that version — the
          wording the person was actually given — rather than whatever the form says today.
        </p>
        <div className="rn-row" style={{ gap: 'var(--space-8)', marginTop: 'var(--space-8)' }}>
          <button
            type="button"
            className="rn-btn-quiet"
            onClick={() => navigate('/compliance/forms/responses')}
          >
            Open a response
          </button>
        </div>
        {responseGate.kind === 'refused' ? (
          // NOT hidden. A reader who cannot open responses should be told that,
          // not left to conclude there are none to open. `refused` and not
          // `!allowed`: the other two states are not answers about the account.
          <p className="rn-muted" role="note" data-response-gate="refused">
            Your account cannot read form responses. That is a permission your organization
            controls; it is not a statement that no responses exist.
          </p>
        ) : responseGate.kind === 'allowed' ? null : (
          <p className="rn-muted" role="status" data-response-gate={responseGate.kind}>
            {gateSentence(responseGate, 'read form responses')}
          </p>
        )}

        {canAuthor && creating ? (
          <form
            className="rn-col"
            style={{ gap: 'var(--space-12)', marginTop: 'var(--space-16)' }}
            onSubmit={(e) => {
              e.preventDefault();
              if (formKeyProblem === null && newForm.title.trim() !== '') create.mutate();
            }}
          >
            <SettingsField
              label="Title"
              hint="What people will call it. This can change; the key cannot."
            >
              <input
                type="text"
                value={newForm.title}
                onChange={(e) => setNewForm({ ...newForm, title: e.currentTarget.value })}
              />
            </SettingsField>
            <SettingsField
              label="Key"
              hint="The stable name it keeps forever. Lower-case letters, digits, hyphens and underscores."
              error={newForm.formKey === '' ? undefined : (formKeyProblem ?? undefined)}
            >
              <input
                type="text"
                value={newForm.formKey}
                spellCheck={false}
                onChange={(e) => setNewForm({ ...newForm, formKey: e.currentTarget.value })}
              />
            </SettingsField>
            <SettingsField label="What it is for" hint="Optional. Read by whoever approves it.">
              <textarea
                rows={2}
                value={newForm.purpose}
                onChange={(e) => setNewForm({ ...newForm, purpose: e.currentTarget.value })}
              />
            </SettingsField>
            <SettingsField label="Kind">
              <select
                value={newForm.instrumentKind}
                onChange={(e) =>
                  setNewForm({
                    ...newForm,
                    instrumentKind: e.currentTarget.value as forms.FormInstrumentKind,
                  })
                }
              >
                {(Object.keys(INSTRUMENT_READING) as forms.FormInstrumentKind[]).map((k) => (
                  <option key={k} value={k}>
                    {INSTRUMENT_READING[k]}
                  </option>
                ))}
              </select>
            </SettingsField>
            <fieldset style={{ border: 0, padding: 0, display: 'grid', gap: 'var(--space-6)' }}>
              <legend className="rn-muted">Who or what it can be about</legend>
              {(Object.keys(SUBJECT_READING) as forms.FormSubjectKind[]).map((kind) => (
                <label
                  key={kind}
                  className="rn-row"
                  style={{ gap: 'var(--space-8)', alignItems: 'center' }}
                >
                  <input
                    type="checkbox"
                    checked={newForm.subjectKinds.includes(kind)}
                    onChange={(e) =>
                      setNewForm({
                        ...newForm,
                        subjectKinds: e.currentTarget.checked
                          ? [...newForm.subjectKinds, kind]
                          : newForm.subjectKinds.filter((k) => k !== kind),
                      })
                    }
                  />
                  <span>{SUBJECT_READING[kind]}</span>
                </label>
              ))}
            </fieldset>
            {createError ? (
              <p role="alert" style={{ color: 'var(--color-urgent-deep)' }}>
                {createError}
              </p>
            ) : null}
            <div className="rn-row" style={{ gap: 'var(--space-8)' }}>
              <button
                type="submit"
                className="rn-btn"
                disabled={
                  create.isPending ||
                  formKeyProblem !== null ||
                  newForm.title.trim() === '' ||
                  newForm.subjectKinds.length === 0
                }
              >
                Create it
              </button>
              <button
                type="button"
                className="rn-btn-quiet"
                onClick={() => {
                  setCreating(false);
                  setCreateError(null);
                }}
              >
                Cancel
              </button>
            </div>
            <p className="rn-muted">
              This creates the form and nothing else. The questions are a version, and a version
              needs a person&rsquo;s approval before anything can be asked with it.
            </p>
          </form>
        ) : null}
      </SettingsCard>

      <SettingsCard>
        <div className="cmp-control-head">
          {/* Roving tabindex, Home/End and arrow keys come from the shared
              hook. A strip that wires its own gets the role without the
              behaviour, which is a promise to assistive technology that the
              keyboard does not keep. */}
          <div role="tablist" aria-label="Which forms" className="cmp-tabs" {...tabKeys}>
            {TABS.map((entry) => (
              <button
                key={entry.key}
                type="button"
                role="tab"
                id={`forms-tab-${entry.key}`}
                aria-controls="forms-panel"
                aria-selected={tab === entry.key}
                className="rn-tab-btn"
                onClick={() => setTab(entry.key)}
              >
                {entry.label}
              </button>
            ))}
          </div>
          <label className="rn-row" style={{ gap: 'var(--space-8)', alignItems: 'center' }}>
            <span className="rn-muted">Kind</span>
            <select
              value={kind}
              onChange={(e) => setKind(e.currentTarget.value as forms.FormInstrumentKind | '')}
            >
              <option value="">Any</option>
              {(Object.keys(INSTRUMENT_READING) as forms.FormInstrumentKind[]).map((k) => (
                <option key={k} value={k}>
                  {INSTRUMENT_READING[k]}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div id="forms-panel" role="tabpanel" aria-labelledby={`forms-tab-${tab}`}>
          <ListState
            phase={phase}
            surface="table"
            rows={5}
            failedMessage="We couldn’t load your forms just now. Do not read this as an organization with no instruments."
            onRetry={() => void list.refetch()}
            empty={
              filtered ? (
                <SuccessEmpty
                  heading="Nothing matches this filter"
                  message="Widen the filter — there may be forms outside it, including retired ones."
                />
              ) : (
                <SuccessEmpty
                  heading="No forms yet"
                  message={
                    canAuthor
                      ? 'Nothing has been authored. A form starts as a draft, goes to a person for approval, and only then can be published.'
                      : 'Nothing has been authored yet.'
                  }
                />
              )
            }
          >
            <TableCard
              columns={columns}
              rows={definitions}
              rowKey={(d) => d.id}
              onRowOpen={(d) => navigate(`/compliance/forms/${d.id}`)}
              caption="Forms"
            />
          </ListState>
        </div>

        {/* Said once, at the bottom, rather than as a column that would repeat a
            constant on every row. It is the load-bearing rule of the domain and
            the reason the detail screen has two buttons instead of one. */}
        <p className="rn-muted" style={{ marginTop: 'var(--space-12)' }}>
          Approving and publishing are separate steps. An approval is bound to the exact wording it
          approved, so editing an approved form produces a new version that needs its own approval —
          there is no way to change a published question without somebody signing for the change.
        </p>
      </SettingsCard>
    </div>
  );
}
